    import React, { useMemo, useRef, useState } from 'react';
    import { useTranslation } from 'react-i18next';
    import AutoComplete from 'src/common/refactoredComponents/form/autocomplete/AutoComplete';
    import DownArrowFilter from 'src/assets/images/DownArrowFilter.svg';
    import { Dot } from 'src/styles/CommonStyles.styled';
    import { accountNumberZeroTrimmer,debounce } from 'src/utils/util';
    import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
    import {LocationFilterWrapper, LocationHeader, LocationHeaderLeft, LocationTitle, LocationChevron, LocationContent, LocationRow, LocationLabel, SelectWrapper, AccountOptionRow, AccountOptionName, AccountOptionNumber} from './index.styled';
    import { fetchFilterData } from 'src/pages/receiveSets/redux/actions';
    import { useDispatch, useSelector } from 'react-redux';

    const LocationFilter = ({ 
        title, 
        viewKey, 
        territoryField, 
        accountField, 
        isApplied,
        isExpanded, 
        filterState,
        updateExpandedViews,
        districtCode,
        territoryFilters,
        accountFilters,
        handleLocationDropdownChange
    }) => {
        const { t } = useTranslation();
        const dispatch = useDispatch();
        const accountFilterLoading = useSelector((state) => state.consignedInventoryReducer.filterLoading.accountFilters);

        const territoryOptions = useMemo(() => (territoryFilters || []).map((option) => ({
            ...option,
            displayLabel: option.filterName === 'All' ? t('common.allLabel') : option.filterName,
        })), [territoryFilters, t]);

        const accountOptions = useMemo(() => (accountFilters || []).map((option) => ({
            ...option,
            displayLabel: option.filterName === 'All' ? t('common.allLabel') : option.filterName,
        })), [accountFilters, t]);

        const selectedTerritory = useMemo(
            () => territoryOptions.find((option) => option.filterCode === filterState[territoryField]) || null,
            [territoryOptions, filterState, territoryField]
        );

        const selectedAccount = useMemo(() => {
            const currentValue = filterState?.[accountField];
            const matched = accountOptions.find((option) => option.filterCode === currentValue) || null;

            if (matched) {
                return matched;
            }

            if (!currentValue || currentValue === 'All') {
                return {
                    filterCode: 'All',
                    filterName: 'All',
                    displayLabel: t('common.allLabel'),
                };
            }

            return null;
        }, [accountOptions, filterState, accountField, t]);

        const [territoryOpen, setTerritoryOpen] = useState(false);
        const [accountOpen, setAccountOpen] = useState(false);
        const [territoryInputValue, setTerritoryInputValue] = useState('');
        const [accountInputValue, setAccountInputValue] = useState('');
        const accountClearTriggeredRef = useRef(false);

        useEffectWithErrorLogging(() => {
            setTerritoryInputValue(selectedTerritory?.displayLabel || '');
        }, [selectedTerritory?.filterCode, selectedTerritory?.displayLabel]);

        useEffectWithErrorLogging(() => {
            setAccountInputValue(selectedAccount?.displayLabel || '');
        }, [selectedAccount?.filterCode, selectedAccount?.displayLabel]);

        const dropdownTemplate = (option) => {
            if (!option) return null;

            const accountName = option.accountName || option.filterName;
            const accountNumber = option.accountId || option.filterCode;

            return (
                <AccountOptionRow>
                    <AccountOptionName>
                        {accountName === 'All' ? t('common.allLabel') : accountName}
                    </AccountOptionName>
                    {accountNumber !== 'All' ? (
                        <AccountOptionNumber>
                            {accountNumberZeroTrimmer(accountNumber)}
                        </AccountOptionNumber>
                    ) : null}
                </AccountOptionRow>
            );
        };

        const normalizeAccountValue = (value) => `${value || ''}`.trim().replace(/^0+/, '').toLowerCase();

        const findMatchingAccountOption = (input) => {
            const normalizedInput = normalizeAccountValue(input);

            if (!normalizedInput) {
                return null;
            }

            return accountOptions.find((option) => {
                const candidates = [
                    option.accountId,
                    option.filterCode,
                    option.accountName,
                    option.filterName,
                    option.displayLabel,
                ];

                return candidates.some((candidate) => normalizeAccountValue(candidate) === normalizedInput);
            }) || null;
        };

        const buildAccountParams = (searchParam = '') => ({
            ...(districtCode ? { district: districtCode } : {}),
            ...(filterState[territoryField] && filterState[territoryField] !== 'All'
                ? { territory: filterState[territoryField] }
                : {}),
            ...(searchParam ? { searchParam } : {}),
        });

        const debouncedFilterSearch = useRef(
            debounce((value) => {
                 dispatch(fetchFilterData('accountFilters', value, accountField));
            }, 700)
        ).current;

        const refreshAccountOptions = (searchParam = '') => {
            dispatch(fetchFilterData('accountFilters', buildAccountParams(searchParam), accountField));
        };


        const handleTerritoryChange = (option) => {
            handleLocationDropdownChange(territoryField, option ? option.filterCode : 'All');
        };

        const handleAccountChange = (option, reason) => {
            accountClearTriggeredRef.current = reason === 'clear';
            handleLocationDropdownChange(accountField, option ? option.filterCode : 'All');
        };

        const handleTerritoryInputChange = (event, newInputValue, reason) => {
            if (reason === 'input') {
                setTerritoryInputValue(newInputValue);
            }
            if (reason === 'clear') {
                setTerritoryInputValue('');
            }
        };

        const handleAccountInputChange = (event, newInputValue, reason) => {
            if (reason === 'input') {
                accountClearTriggeredRef.current = false;
                setAccountInputValue(newInputValue);
                if (newInputValue && newInputValue.trim()) {
                    debouncedFilterSearch(buildAccountParams(newInputValue));
                    return;
                }
                refreshAccountOptions();
            }
            if (reason === 'clear') {
                accountClearTriggeredRef.current = true;
                setAccountInputValue('');
                refreshAccountOptions();
            }
        };

        const handleTerritoryBlur = () => {
            setTerritoryOpen(false);
            setTerritoryInputValue(selectedTerritory?.displayLabel || '');
        };

        const handleAccountBlur = () => {
            setAccountOpen(false);
            if (accountClearTriggeredRef.current) {
                accountClearTriggeredRef.current = false;
                return;
            }

            const matchingAccount = findMatchingAccountOption(accountInputValue);

            if (matchingAccount) {
                setAccountInputValue(matchingAccount.displayLabel || '');
                if (matchingAccount.filterCode !== filterState[accountField]) {
                    handleLocationDropdownChange(accountField, matchingAccount.filterCode);
                }
                return;
            }

            setAccountInputValue(selectedAccount?.displayLabel || '');
        };  

        const handleAccountFocus = () => {
            accountClearTriggeredRef.current = false;
            setAccountOpen(true);
        };

        return (
            <>
                <LocationFilterWrapper>
                    <LocationHeader 
                        isExpanded={isExpanded}
                        onClick={() => updateExpandedViews({ view: viewKey, value: !isExpanded })}
                    >
                       <LocationTitle>{title}</LocationTitle>
                      <LocationHeaderLeft>
                        {!isExpanded && isApplied && <Dot/>}
                        <LocationChevron 
                            src={DownArrowFilter} 
                            alt="toggle" 
                            isExpanded={isExpanded}
                        />
                         </LocationHeaderLeft>
                    </LocationHeader>
                    <LocationContent isExpanded={isExpanded}>
                        <LocationRow>
                            <LocationLabel>{t('filter.filterByTerritory')}</LocationLabel>
                            <SelectWrapper>
                                <AutoComplete
                                    id={`${viewKey}-territory`}
                                    options={territoryOptions}
                                    field="displayLabel"
                                    filterField="displayLabel"
                                    optionValue="filterCode"
                                    value={selectedTerritory}
                                    inputValue={territoryInputValue}
                                    onChange={handleTerritoryChange}
                                    onInputChange={handleTerritoryInputChange}
                                    open={territoryOpen}
                                    onOpen={() => setTerritoryOpen(true)}
                                    onClose={() => setTerritoryOpen(false)}
                                    onFocus={() => setTerritoryOpen(true)}
                                    onBlur={handleTerritoryBlur}
                                    itemTemplate={dropdownTemplate}
                                    height="40px"
                                    width="100%"
                                    placeholder="Select Territory"
                                    mbBorderRadius={'6px'}
                                    boxShadow={'0px 3px 6px #00000029'}
                                    detailsMargin
                                    marginLeft={'1.5px'}
                                    zIndex={'100000'}
                                    listBoxProps={{ style: { maxHeight: 260, overflow: 'hidden' } }}
                                    showScrollBar
                                    disableClearable={false}
                                    disabled={territoryFilters.length === 1}
                                />
                            </SelectWrapper>
                        </LocationRow>
                        <LocationRow>
                            <LocationLabel>{t('common.accountLabel')}</LocationLabel>
                            <SelectWrapper>
                                <AutoComplete
                                    id={`${viewKey}-account`}
                                    loading={accountFilterLoading}
                                    options={accountOptions}
                                    field="displayLabel"
                                    filterField="displayLabel"
                                    optionValue="filterCode"
                                    value={selectedAccount}
                                    inputValue={accountInputValue}
                                    onChange={handleAccountChange}
                                    onInputChange={handleAccountInputChange}
                                    open={accountOpen}
                                    onOpen={() => {
                                        setAccountOpen(true);
                                    }}
                                    onClose={() => setAccountOpen(false)}
                                    onFocus={handleAccountFocus}
                                    onBlur={handleAccountBlur}
                                    itemTemplate={dropdownTemplate}
                                    height="40px"
                                    width="100%"
                                    placeholder="Select Account"
                                    mbBorderRadius={'6px'}
                                    boxShadow={'0px 3px 6px #00000029'}
                                    detailsMargin
                                    marginLeft={'1.5px'}
                                    zIndex={'100000'}
                                    listBoxProps={{ style: { maxHeight: 260, overflow: 'auto' } }}
                                    showScrollBar
                                    disableClearable={false}
                                    //disabled={accountFilters.length === 1}
                                />
                            </SelectWrapper>
                        </LocationRow>
                    </LocationContent>
                </LocationFilterWrapper>
            </>
        );
    };

    export default LocationFilter;